import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import NavigationMenu from '../../components/NavigationMenu/NavigationMenu';
import { createFantasyInvoice, listFantasyRequests, updateFantasyRequest } from '../../../services/SubmissionService';
import { listCities } from '../../../services/UserService';
import kiLogo from '../../../assets/ki-logo.svg';
import './Fantasy.css';

const formatCurrency = (value) => value === null || value === undefined || value === ''
    ? '—'
    : `€${Number(value).toLocaleString('en-US')}`;
const budgetLabels = {
    '1-3k': '€1,000 – €3,000',
    '3-5k': '€3,000 – €5,000',
    '5-10k': '€5,000 – €10,000',
    '10-25k': '€10,000 – €25,000',
    '25k+': '€25,000+'
};
const formatBudget = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    const normalized = String(value).replace(/[€$,\s]/g, '');
    const label = budgetLabels[String(value).toLowerCase()] || budgetLabels[Object.keys(budgetLabels).find(key => key === normalized.toLowerCase())];
    if (label) return label;
    const rangeMatch = normalized.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) return `€${Number(rangeMatch[1]).toLocaleString('en-US')} – €${Number(rangeMatch[2]).toLocaleString('en-US')}`;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? formatCurrency(numeric) : String(value);
};
const invoiceAmount = (value) => {
    const numeric = Number(value);
    return value !== null && value !== undefined && value !== '' && Number.isFinite(numeric) ? numeric : '';
};
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const getStatusClass = (status) => String(status || 'unknown').toLowerCase().replace(/\s+/g, '-');
const getRequestName = (request) => request.client || request.title || 'Unnamed request';
const getRequestId = (request) => request.uuid || request.id;
const requestValue = (request, field) => request[field] ?? request.data?.[field];
const getRequestEmail = (request) => requestValue(request, 'email');
const getRequestPhone = (request) => requestValue(request, 'phone');
const phoneDigits = (value) => String(value || '').replace(/\D/g, '');
const renderEmail = (value) => value
    ? <a className="email-link" href={`mailto:${value}`} title={value}>{value}</a>
    : '—';
const renderPhone = (value) => {
    if (!value) return '—';
    const whatsappNumber = phoneDigits(value);
    return (
        <span className="contact-links">
            <a href={`tel:${String(value).replace(/[^\d+]/g, '')}`}>{value}</a>
            {whatsappNumber && <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noreferrer">WhatsApp</a>}
        </span>
    );
};
const statuses = ['Pending', 'Approved', 'Completed', 'Rejected'];
const detailSummaryFields = new Set(['uuid', 'id', 'client', 'title', 'email', 'phone', 'city', 'cityUuid', 'date', 'status', 'budget', 'createdAt', 'updatedAt', 'model']);
const formatDetailLabel = (field) => field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, character => character.toUpperCase());
const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.length ? value.map(item => typeof item === 'object' ? JSON.stringify(item) : item).join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};
const getRequestDetails = (request) => {
    const source = request.data && typeof request.data === 'object' && !Array.isArray(request.data) ? request.data : request;
    return Object.entries(source).filter(([field]) => !detailSummaryFields.has(field));
};
const today = () => new Date().toISOString().split('T')[0];
const dueDate = () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
const loadErrorMessage = (error) => error instanceof TypeError
    ? 'Unable to connect to the fantasy requests service.'
    : error.message || 'Unable to load fantasy requests.';

const Fantasy = () => {
    const [requests, setRequests] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [statusValue, setStatusValue] = useState('Pending');
    const [isSavingStatus, setIsSavingStatus] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [notice, setNotice] = useState('');
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [invoiceError, setInvoiceError] = useState('');
    const [invoiceData, setInvoiceData] = useState({
        invoiceNumber: 'INV-001', date: today(), dueDate: dueDate(), clientName: '',
        clientAddress: '', serviceDescription: '', amount: '', vatRate: 20, notes: ''
    });
    const invoiceRef = useRef(null);

    const loadRequests = async () => {
        setError('');
        setIsLoading(true);
        try {
            const result = await listFantasyRequests();
            setRequests(result.items);
        } catch (loadError) {
            setError(loadErrorMessage(loadError));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { 
        loadRequests(); 
    }, []);

    useEffect(() => {
        let isMounted = true;
        listCities()
            .then(items => {
                if (isMounted) setCities(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (isMounted) setCities([]);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    const cityNames = Object.fromEntries(cities.map(city => [city.uuid, city.name || city.title || city.slug]));
    const getCityName = request => cityNames[requestValue(request, 'cityUuid')] || requestValue(request, 'city') || '—';

    const openRequest = (request) => {
        setSelectedRequest(request);
        setStatusValue(requestValue(request, 'status') || 'Pending');
        setStatusError('');
        setNotice('');
    };

    const saveStatus = async () => {
        const requestId = selectedRequest && getRequestId(selectedRequest);
        if (!requestId || !statusValue) return;
        setIsSavingStatus(true);
        setStatusError('');
        try {
            const updatedRequest = await updateFantasyRequest(requestId, { status: statusValue });
            const savedStatus = updatedRequest?.status || statusValue;
            setRequests(current => current.map(item => getRequestId(item) === requestId
                ? { ...item, ...updatedRequest, status: savedStatus }
                : item));
            setSelectedRequest(current => ({ ...current, ...updatedRequest, status: savedStatus }));
            setNotice(`Fantasy request status updated to ${savedStatus}.`);
        } catch (saveError) {
            setStatusError(loadErrorMessage(saveError));
        } finally {
            setIsSavingStatus(false);
        }
    };

    const handleCreateInvoice = (request) => {
        setInvoiceError('');
        setInvoiceData({
            invoiceNumber: `INV-${getRequestId(request)}`,
            date: today(),
            dueDate: dueDate(),
            clientName: getRequestName(request),
            clientAddress: '',
            serviceDescription: `Fantasy booking${request.model ? ` with model ${request.model}` : ''}`,
            amount: invoiceAmount(request.budget),
            vatRate: 20,
            notes: ''
        });
        setShowInvoiceModal(true);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setInvoiceData(previous => ({ ...previous, [name]: value }));
    };

    const generatePdf = async (invoice) => {
        if (!invoiceRef.current) return;
        const canvas = await html2canvas(invoiceRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imageWidth = 190;
        const pageHeight = 295;
        const imageHeight = (canvas.height * imageWidth) / canvas.width;
        let heightLeft = imageHeight;
        let position = 10;
        const image = canvas.toDataURL('image/png');
        pdf.addImage(image, 'PNG', 10, position, imageWidth, imageHeight);
        heightLeft -= pageHeight;
        while (heightLeft >= 0) {
            position = heightLeft - imageHeight;
            pdf.addPage();
            pdf.addImage(image, 'PNG', 10, position, imageWidth, imageHeight);
            heightLeft -= pageHeight;
        }
        pdf.save(`invoice-${invoice.invoiceNumber}.pdf`);
    };

    const handleGeneratePDF = async () => {
        const requestId = selectedRequest && getRequestId(selectedRequest);
        if (!requestId) {
            setInvoiceError('This request has no UUID and cannot be invoiced.');
            return;
        }
        setInvoiceError('');
        try {
            const invoice = await createFantasyInvoice(requestId, {
                ...invoiceData,
                amount: Number(invoiceData.amount) || 0,
                vatRate: Number(invoiceData.vatRate) || 0
            });
            await generatePdf({ ...invoiceData, ...invoice });
            setShowInvoiceModal(false);
        } catch (invoiceRequestError) {
            setInvoiceError(loadErrorMessage(invoiceRequestError));
        }
    };

    const closeInvoice = () => {
        setShowInvoiceModal(false);
        setInvoiceError('');
    };

    const vat = (Number(invoiceData.amount) || 0) * (Number(invoiceData.vatRate) || 0) / 100;
    const total = (Number(invoiceData.amount) || 0) + vat;

    return (
        <div className="fantasy">
            <NavigationMenu />
            <div className="fantasy-content">
                <header className="fantasy-header"><h1 className="fantasy-title">Fantasy Requests</h1><p className="fantasy-subtitle">Manage client fantasy bookings and requests</p></header>
                <main className="fantasy-main">
                    {isLoading ? <div className="submission-feedback">Loading fantasy requests...</div> : error ? (
                        <div className="submission-feedback submission-error" role="alert"><p>{error}</p><button type="button" className="feedback-button" onClick={loadRequests}>Try again</button></div>
                    ) : requests.length === 0 ? (
                        <div className="submission-empty"><h2>No fantasy requests</h2><p>New requests will appear here when they are submitted.</p></div>
                    ) : (
                        <div className="table-container">
                            <div className="table-header"><div><h2 className="table-title">All Requests</h2><p className="table-caption">{requests.length} request{requests.length === 1 ? '' : 's'}</p></div></div>
                            <div className="table-scroll"><table className="data-table"><thead><tr><th>Client</th><th>Email</th><th>Phone</th><th>City</th><th>Date</th><th>Status</th><th>Budget</th><th>Actions</th></tr></thead><tbody>
                                {requests.map(request => <tr key={getRequestId(request)}><td>{getRequestName(request)}</td><td>{renderEmail(getRequestEmail(request))}</td><td>{renderPhone(getRequestPhone(request))}</td><td>{getCityName(request)}</td><td>{request.date || '—'}</td><td><span className={`status-badge ${getStatusClass(request.status)}`}>{request.status || 'Unknown'}</span></td><td>{formatBudget(request.budget)}</td><td><button type="button" className="action-btn view" onClick={() => openRequest(request)}>View</button><button type="button" className="action-btn process" onClick={() => { setSelectedRequest(request); handleCreateInvoice(request); }}>Create Invoice</button></td></tr>)}
                            </tbody></table></div>
                        </div>
                    )}
                </main>
            </div>

            {selectedRequest && !showInvoiceModal && <div className="fantasy-modal-overlay" role="presentation"><div className="fantasy-modal" role="dialog" aria-modal="true" aria-labelledby="fantasy-request-title"><div className="modal-header"><div><span className="modal-kicker">Request {getRequestId(selectedRequest)}</span><h3 className="modal-title" id="fantasy-request-title">{getRequestName(selectedRequest)}</h3></div><button type="button" className="modal-close" onClick={() => setSelectedRequest(null)} aria-label="Close request details">×</button></div><div className="modal-body"><div className="detail-grid"><div><span>Email</span><strong>{renderEmail(getRequestEmail(selectedRequest))}</strong></div><div><span>Phone</span><strong>{renderPhone(getRequestPhone(selectedRequest))}</strong></div><div><span>Model</span><strong>{requestValue(selectedRequest, 'model') || '—'}</strong></div><div><span>City</span><strong>{getCityName(selectedRequest)}</strong></div><div><span>Date</span><strong>{requestValue(selectedRequest, 'date') || '—'}</strong></div><div><span>Status</span><strong><span className={`status-badge ${getStatusClass(requestValue(selectedRequest, 'status'))}`}>{requestValue(selectedRequest, 'status') || 'Unknown'}</span></strong></div><div><span>Budget</span><strong>{formatBudget(requestValue(selectedRequest, 'budget'))}</strong></div></div><div className="detail-block"><span>All request details</span><div className="detail-grid detail-fields">{getRequestDetails(selectedRequest).map(([field, value]) => <div key={field}><span>{formatDetailLabel(field)}</span><strong>{formatDetailValue(value)}</strong></div>)}</div>{getRequestDetails(selectedRequest).length === 0 && <p>No additional details were provided.</p>}</div><div className="status-editor"><label htmlFor="fantasy-status">Request status</label><select id="fantasy-status" className="modal-input" value={statusValue} onChange={event => setStatusValue(event.target.value)}>{statuses.map(status => <option key={status} value={status}>{status}</option>)}</select>{notice && <p className="status-notice" role="status">{notice}</p>}{statusError && <p className="field-error" role="alert">{statusError}</p>}</div></div><div className="modal-footer"><button type="button" className="modal-button cancel" onClick={() => setSelectedRequest(null)}>Close</button><button type="button" className="modal-button create" onClick={saveStatus} disabled={isSavingStatus}>{isSavingStatus ? 'Saving...' : 'Save Status'}</button><button type="button" className="modal-button create" onClick={() => handleCreateInvoice(selectedRequest)}>Create Invoice</button></div></div></div>}

            {showInvoiceModal && <div className="fantasy-modal-overlay" role="presentation"><div className="fantasy-modal invoice-modal" role="dialog" aria-modal="true" aria-labelledby="invoice-title"><div className="modal-header"><h3 className="modal-title" id="invoice-title">Create Invoice</h3><button type="button" className="modal-close" onClick={closeInvoice} aria-label="Close invoice form">×</button></div><div className="invoice-content"><div className="invoice-form"><div className="form-group"><label htmlFor="invoice-number">Invoice number</label><input id="invoice-number" className="modal-input" name="invoiceNumber" value={invoiceData.invoiceNumber} onChange={handleInputChange} /></div><div className="form-row"><div className="form-group"><label htmlFor="invoice-date">Date</label><input id="invoice-date" className="modal-input" type="date" name="date" value={invoiceData.date} onChange={handleInputChange} /></div><div className="form-group"><label htmlFor="invoice-due-date">Due date</label><input id="invoice-due-date" className="modal-input" type="date" name="dueDate" value={invoiceData.dueDate} onChange={handleInputChange} /></div></div><div className="form-group"><label htmlFor="client-name">Client name</label><input id="client-name" className="modal-input" name="clientName" value={invoiceData.clientName} onChange={handleInputChange} /></div><div className="form-group"><label htmlFor="client-address">Client address</label><textarea id="client-address" className="modal-input" name="clientAddress" rows="2" value={invoiceData.clientAddress} onChange={handleInputChange} /></div><div className="form-group"><label htmlFor="service-description">Service description</label><textarea id="service-description" className="modal-input" name="serviceDescription" rows="3" value={invoiceData.serviceDescription} onChange={handleInputChange} /></div><div className="form-row"><div className="form-group"><label htmlFor="invoice-amount">Amount (€)</label><input id="invoice-amount" className="modal-input" type="number" min="0" name="amount" value={invoiceData.amount} onChange={handleInputChange} /></div><div className="form-group"><label htmlFor="invoice-vat">VAT rate (%)</label><input id="invoice-vat" className="modal-input" type="number" min="0" name="vatRate" value={invoiceData.vatRate} onChange={handleInputChange} /></div></div><div className="form-group"><label htmlFor="invoice-notes">Notes</label><textarea id="invoice-notes" className="modal-input" name="notes" rows="2" value={invoiceData.notes} onChange={handleInputChange} /></div></div><aside className="invoice-summary"><h4>Invoice summary</h4><div className="invoice-preview"><div><span>Subtotal</span><strong>€{formatNumber(invoiceData.amount)}</strong></div><div><span>VAT ({invoiceData.vatRate}%)</span><strong>€{formatNumber(vat)}</strong></div><div className="summary-total"><span>Total</span><strong>€{formatNumber(total)}</strong></div></div><h4>Company details</h4><p>Korina International Limited<br />128 City Road<br />London, United Kingdom</p></aside></div>{invoiceError && <div className="modal-error" role="alert">{invoiceError}</div>}<div className="modal-footer"><button type="button" className="modal-button cancel" onClick={closeInvoice}>Cancel</button><button type="button" className="modal-button create" onClick={handleGeneratePDF}>Create &amp; Download PDF</button></div></div></div>}

            <div className="invoice-document" ref={invoiceRef} aria-hidden="true"><div className="invoice-document-header"><div><img src={kiLogo} alt="KI Logo" /><strong>Korina International</strong><p>128 City Road<br />London, United Kingdom</p></div><div><b>INVOICE</b><span>#{invoiceData.invoiceNumber}</span></div></div><div className="invoice-document-meta"><div><small>Bill to</small><strong>{invoiceData.clientName}</strong><p>{invoiceData.clientAddress}</p></div><div><small>Invoice date</small><span>{invoiceData.date}</span><small>Due date</small><span>{invoiceData.dueDate}</span></div></div><div className="invoice-document-service"><small>Service description</small><p>{invoiceData.serviceDescription}</p></div><div className="invoice-document-total"><span>Amount</span><strong>€{formatNumber(invoiceData.amount)}</strong><span>VAT ({invoiceData.vatRate}%)</span><strong>€{formatNumber(vat)}</strong><b>Total</b><b>€{formatNumber(total)}</b></div>{invoiceData.notes && <div className="invoice-document-service"><small>Notes</small><p>{invoiceData.notes}</p></div>}<footer>Korina International Limited · 128 City Road, London, United Kingdom</footer></div>
        </div>
    );
};

export default Fantasy;